import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, Segmented, Select, Switch } from 'antd';
import useFilterOptions from './useFilterOptions';
import { FilterField, FilterOption, SelectFilterField } from './types';

const { RangePicker } = DatePicker;

/**
 * The controls behind a filter schema, plus the value-writing rules they share.
 *
 * Three layouts render the same fields — the inline bar, the filter drawer and a
 * table's column headers — so both the markup and the cascade semantics live
 * here rather than in whichever one happened to need them first.
 */

export const isEmpty = (value: any) =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** Plain text for an option whose label may be a node (a tag, say). */
export const optionText = (option?: FilterOption) =>
    option ? (option.text ?? (typeof option.label === 'string' ? option.label : String(option.value))) : undefined;

/**
 * An unset select means "no restriction", so say so: "Application" reads as
 * "All applications". An explicit `placeholder` on the field always wins.
 */
const allPlaceholder = (label: string) => {
    const plural = /s$/i.test(label) ? label : `${label}s`;
    return `All ${plural.toLowerCase()}`;
};

/** Filter keys a field owns — a range owns two, everything else one. */
export const fieldKeys = (field: FilterField): string[] =>
    field.type === 'dateRange' ? [field.startName, field.endName] : [field.name];

/** True while this field is narrowing the result set. */
export const isFieldActive = (field: FilterField, values: Record<string, any>) =>
    fieldKeys(field).some((key) => !isEmpty(values[key]));

/**
 * How many values the field is holding — a multi-select counts each pick, so a
 * header can say "3" rather than only that something is set.
 */
export const activeValueCount = (field: FilterField, values: Record<string, any>): number => {
    if (!isFieldActive(field, values)) return 0;
    if (field.type === 'select' && field.multiple) {
        const raw = values[field.name];
        return Array.isArray(raw) ? raw.length : String(raw).split(',').length;
    }
    return 1;
};

/* -------------------------------------------------------------------------- */
/*  Value writers                                                             */
/* -------------------------------------------------------------------------- */

/**
 * A cascade parent changing invalidates everything downstream of it, not just
 * its immediate children: in consumer → application → service item, picking a
 * new consumer must drop the service item too, or a stale grandchild keeps
 * filtering against a parent that no longer applies.
 */
const clearDescendants = (fields: FilterField[], next: Record<string, any>, changed: string) => {
    const seen = new Set<string>([changed]);
    const queue = [changed];
    while (queue.length) {
        const parent = queue.shift() as string;
        fields.forEach((field) => {
            if (field.type !== 'select' || !field.dependsOn?.includes(parent)) return;
            if (seen.has(field.name)) return; // also guards a cyclic schema
            seen.add(field.name);
            delete next[field.name];
            queue.push(field.name);
        });
    }
};

/**
 * The one writer every layout goes through, so cascade clearing cannot differ
 * between the bar, the drawer and a column header.
 */
export const createSetValue = (
    fields: FilterField[],
    values: Record<string, any>,
    onChange: (next: Record<string, any>) => void,
) => (name: string, value: any) => {
    const next = { ...values };
    if (isEmpty(value)) delete next[name];
    else next[name] = value;

    clearDescendants(fields, next, name);
    onChange(next);
};

const createSetRange = (
    values: Record<string, any>,
    onChange: (next: Record<string, any>) => void,
) => (start: string, end: string, dates: any) => {
    const next = { ...values };
    const from = dates?.[0];
    const to = dates?.[1];
    if (from) next[start] = from.startOf?.('day')?.toISOString?.() ?? from.toISOString();
    else delete next[start];
    if (to) next[end] = to.endOf?.('day')?.toISOString?.() ?? to.toISOString();
    else delete next[end];
    onChange(next);
};

/**
 * Drop every value the given fields own, and everything that cascades from
 * them — clearing a parent has to invalidate its children however it is
 * cleared, whether from a chip, a column header or "Clear all".
 */
export const clearFields = (
    fields: FilterField[],
    schema: FilterField[],
    values: Record<string, any>,
    onChange: (next: Record<string, any>) => void,
) => {
    const next = { ...values };
    fields.forEach((field) => {
        fieldKeys(field).forEach((key) => delete next[key]);
        clearDescendants(schema, next, field.name);
    });
    onChange(next);
};

/* -------------------------------------------------------------------------- */
/*  Controls                                                                  */
/* -------------------------------------------------------------------------- */

const SelectControl = ({
    field,
    values,
    setValue,
    parentLabel,
}: {
    field: SelectFilterField;
    values: Record<string, any>;
    setValue: (name: string, value: any) => void;
    parentLabel?: string;
}) => {
    const { options, loading, disabled } = useFilterOptions(field, values);
    const raw = values[field.name];

    // antd hands optionRender its own shape, so map back to our FilterOption
    const byValue = useMemo(() => {
        const map = new Map<string | number, FilterOption>();
        options.forEach((o) => map.set(o.value, o));
        return map;
    }, [options]);

    const value = field.multiple
        ? (Array.isArray(raw) ? raw : (raw ? String(raw).split(',') : []))
        : (isEmpty(raw) ? undefined : (Array.isArray(raw) ? raw[0] : raw));

    return (
        <Select
            id={field.name}
            data-cy={field.dataCy}
            className="ft-control filter-select"
            mode={field.multiple ? 'multiple' : undefined}
            maxTagCount="responsive"
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            loading={loading}
            disabled={disabled}
            placeholder={disabled
                ? `Select ${(parentLabel ?? field.dependsOn?.[0] ?? 'parent').toLowerCase()} first`
                : (field.placeholder || allPlaceholder(field.label))}
            value={value as any}
            onChange={(next: any) => {
                if (field.multiple) {
                    const list: string[] = next || [];
                    setValue(field.name, list.length === 0 ? undefined : (field.serialize === 'array' ? list : list.join()));
                } else {
                    setValue(field.name, isEmpty(next) ? undefined : next);
                }
            }}
            options={options.map((o) => ({ value: o.value, label: o.label, title: optionText(o) }))}
            optionRender={
                field.optionRender
                    ? (option: any) => {
                        const original = byValue.get(option.value);
                        return original ? field.optionRender!(original) : option.label;
                    }
                    : undefined
            }
        />
    );
};

const SearchControl = ({
    field,
    value,
    setValue,
}: {
    field: Extract<FilterField, { type: 'search' }>;
    value: any;
    setValue: (name: string, value: any) => void;
}) => {
    const [draft, setDraft] = useState<string>(value ?? '');
    const timer = useRef<any>(null);
    const committed = useRef<string>(value ?? '');

    // adopt outside changes (cleared chip, reset) without fighting the user
    useEffect(() => {
        const incoming = value ?? '';
        if (incoming !== committed.current) {
            committed.current = incoming;
            setDraft(incoming);
        }
    }, [value]);

    // a pending debounce must not fire after the control unmounts
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    const commit = (next: string) => {
        committed.current = next;
        setValue(field.name, next === '' ? undefined : next);
    };

    return (
        <Input.Search
            id={field.name}
            data-cy={field.dataCy}
            className="ft-control"
            allowClear
            placeholder={field.placeholder || `Search ${field.label.toLowerCase()}`}
            value={draft}
            onChange={(e) => {
                const next = e.target.value;
                setDraft(next);
                if (timer.current) clearTimeout(timer.current);
                timer.current = setTimeout(() => commit(next), field.debounce ?? 350);
            }}
            onSearch={(next) => {
                if (timer.current) clearTimeout(timer.current);
                commit(next);
            }}
        />
    );
};

/* -------------------------------------------------------------------------- */
/*  Field rendering                                                           */
/* -------------------------------------------------------------------------- */

type FieldProps = {
    field: FilterField;
    /** the whole schema — cascade clearing and parent labels need it */
    schema: FilterField[];
    values: Record<string, any>;
    onChange: (next: Record<string, any>) => void;
};

/** Just the input for a field, with no label around it. */
export const FilterControl = ({ field, schema, values, onChange }: FieldProps) => {
    const setValue = createSetValue(schema, values, onChange);

    switch (field.type) {
        case 'select':
            return (
                <SelectControl
                    field={field}
                    values={values}
                    setValue={setValue}
                    parentLabel={schema.find((f) => f.name === field.dependsOn?.[0])?.label}
                />
            );
        case 'search':
            return <SearchControl field={field} value={values[field.name]} setValue={setValue} />;
        case 'date':
            return (
                <DatePicker
                    id={field.name}
                    data-cy={field.dataCy}
                    className="ft-control"
                    showTime={field.showTime ? { showSecond: false } : false}
                    placeholder={field.placeholder || field.label}
                    onChange={(date: any) => setValue(field.name, date ? date.toISOString() : undefined)}
                />
            );
        case 'dateRange':
            return (
                <RangePicker
                    id={field.name}
                    data-cy={field.dataCy}
                    className="ft-control"
                    allowEmpty={[true, true]}
                    onChange={(dates: any) =>
                        createSetRange(values, onChange)(field.startName, field.endName, dates)}
                />
            );
        case 'toggle':
            return (
                <span className="ft-toggle">
                    <Switch
                        data-cy={field.dataCy}
                        size="small"
                        checked={!isEmpty(values[field.name])}
                        onChange={(checked) => setValue(field.name, checked ? (field.checkedValue ?? true) : undefined)}
                    />
                    <span className="ft-toggle__label">{field.label}</span>
                </span>
            );
        case 'segmented':
            return (
                <Segmented
                    data-cy={field.dataCy}
                    className="ft-control"
                    value={values[field.name] ?? field.options[0]?.value}
                    onChange={(value) => setValue(field.name, value)}
                    options={field.options.map((o) => ({ value: o.value, label: o.label }))}
                />
            );
        case 'custom':
            return <>{field.render()}</>;
        default:
            return null;
    }
};

/** A field with its label above it — the unit both the bar and the drawer lay out. */
export const FilterFieldBlock = ({ field, schema, values, onChange }: FieldProps) => {
    // a toggle carries its own inline label; a second one above it would print
    // the same text twice
    const showLabel = field.type !== 'toggle';
    // `custom` fields are rendered by the caller, so there is no element with
    // this id to point at
    const labelFor = field.type === 'custom' ? undefined : field.name;

    return (
        <div
            className="ft-field"
            style={field.width ? { flexBasis: field.width, maxWidth: field.width } : undefined}
        >
            {showLabel && <label className="ft-field__label" htmlFor={labelFor}>{field.label}</label>}
            <FilterControl field={field} schema={schema} values={values} onChange={onChange} />
        </div>
    );
};
